import React, { useCallback, useEffect, useState } from 'react';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, MarkerType, useReactFlow,
  applyNodeChanges, applyEdgeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import JourneyNode from './JourneyNode';
import { NODE_DEFS } from '../../data/nodeDefinitions';
import styles from './JourneyCanvas.module.css';

const NODE_TYPES = { journeyNode: JourneyNode };
const NODE_W = 200;
const NODE_H = 40; // half-height offset so a dropped node centers under the cursor

function toRFNodes(nodes, selectedId, onDelete) {
  return nodes.map((n) => ({
    id: n.id,
    type: 'journeyNode',
    position: { x: n.x, y: n.y },
    selected: n.id === selectedId,
    data: { nodeType: n.type, cfg: n.cfg, onDelete },
  }));
}

function toRFEdges(edges, selectedEdgeId) {
  return (edges || []).map((e) => ({
    id: e.id,
    source: e.from,
    target: e.to,
    sourceHandle: e.fromHandle || undefined,
    targetHandle: e.toHandle || undefined,
    selected: e.id === selectedEdgeId,
    markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--b2)' },
  }));
}

function CanvasInner(props) {
  const { nodes, edges, selectedId, onSelect, onMove, onDelete, onDeselect, onConnect, onEdgeDelete, onAddAt } = props;
  const { screenToFlowPosition } = useReactFlow();
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);

  // React Flow's own node/edge state, kept local so a drag feels smooth —
  // every mousemove frame updates this directly via applyNodeChanges rather
  // than round-tripping through the parent (JourneyBuilder's setState),
  // which was the actual cause of the reported jank/flicker: dozens of
  // parent re-renders per second, each rebuilding the full node array from
  // scratch, occasionally left this local view and the parent's controlled
  // props briefly out of sync mid-drag (a node would flash out for a frame).
  // Only the final position, once dragging stops, gets reported up via
  // onMove — everything else (add/delete/load/select) still flows down from
  // props as before and re-syncs this local copy.
  const [rfNodes, setRfNodes] = useState(() => toRFNodes(nodes, selectedId, onDelete));
  const [rfEdges, setRfEdges] = useState(() => toRFEdges(edges, selectedEdgeId));

  useEffect(() => {
    setRfNodes(toRFNodes(nodes, selectedId, onDelete));
  }, [nodes, selectedId, onDelete]);

  useEffect(() => {
    setRfEdges(toRFEdges(edges, selectedEdgeId));
  }, [edges, selectedEdgeId]);

  const handleNodesChange = useCallback((changes) => {
    setRfNodes((current) => applyNodeChanges(changes, current));
    changes.forEach((change) => {
      if (change.type === 'position' && change.position && change.dragging === false) {
        onMove(change.id, change.position.x, change.position.y);
      } else if (change.type === 'remove') {
        onDelete(change.id);
      }
    });
  }, [onMove, onDelete]);

  const handleEdgesChange = useCallback((changes) => {
    setRfEdges((current) => applyEdgeChanges(changes, current));
    changes.forEach((change) => {
      if (change.type === 'remove') {
        onEdgeDelete(change.id);
        setSelectedEdgeId((cur) => (cur === change.id ? null : cur));
      }
    });
  }, [onEdgeDelete]);

  const handleConnect = useCallback((connection) => {
    onConnect(connection.source, connection.sourceHandle || null, connection.target);
  }, [onConnect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const nodeType = e.dataTransfer.getData('application/journey-node-type');
    if (!nodeType || !NODE_DEFS[nodeType]) return;
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    onAddAt(nodeType, position.x - NODE_W / 2, position.y - NODE_H / 2);
  }, [screenToFlowPosition, onAddAt]);

  return (
    <div className={styles.wrap} onDrop={handleDrop} onDragOver={handleDragOver}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={handleConnect}
        onNodeClick={(_, node) => { onSelect(node.id); setSelectedEdgeId(null); }}
        onEdgeClick={(_, edge) => setSelectedEdgeId(edge.id)}
        onPaneClick={() => { setSelectedEdgeId(null); onDeselect(); }}
        deleteKeyCode={['Backspace', 'Delete']}
        fitView
        minZoom={0.3}
        maxZoom={1.5}
      >
        <Background variant="dots" gap={24} size={1} color="var(--b1)" />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable style={{ background: 'var(--s2)' }} maskColor="rgba(0,0,0,0.06)" />
      </ReactFlow>

      {nodes.length === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyIco}>
            <span className="material-symbols-rounded" style={{ fontSize: 40 }}>route</span>
          </div>
          <div className={styles.emptyTitle}>Canvas is empty</div>
          <div className={styles.emptySub}>Drag nodes from the left palette to start building your journey</div>
        </div>
      )}
    </div>
  );
}

// ReactFlowProvider is required for useReactFlow() (screenToFlowPosition,
// used by the palette drag/drop handler) to work inside CanvasInner.
function JourneyCanvas(props) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}

export default JourneyCanvas;
